import type { ReactElement } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Button, PlainTooltip } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import LocationService from "pro-features/components/Modals/src/UnlockModal/LocationService";
import { translate } from "app/filters/i18nFilter";
import {
    BannerNoTouchText,
    BannerTrialText,
    BoldLeftDayText,
    colorButtonTrial,
    StyledIconButton,
    TextButtonTrial,
    HomePageBannerWrapper,
    StyledPromotionalPackageText,
    StyledPromotionalIcon,
} from "app/pages/digital-suite/home-page/components/HomePageTrialNTLayout/DigitalSuiteTrialHomePageStyled";
import { allTrackers } from "app/services/track/track";
import { goToPackagesPage } from "app/UtilitiesAndConstants/UtilityFunctions/goToPackagesPage";
import { getDaysLeft } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import { packagesDisplayName } from "app/common/constants/packageNames";

const TrialBannerButton = () => (
    <div data-automation={"manage-plan"}>
        <StyledIconButton
            type="inverted"
            style={{ lineHeight: "normal", background: colorButtonTrial }}
            onClick={() => {
                allTrackers.trackEvent(
                    "Internal Link",
                    "click",
                    `TrialBanner/${LocationService.getCurrentLocationTracking()}`,
                );
                goToPackagesPage();
            }}
            iconName="sparkles"
        >
            <TextButtonTrial>{translate("growth.homepageTrial.buttonText")}</TextButtonTrial>
        </StyledIconButton>
    </div>
);

const NoTouchBannerButton = () => {
    const upgradeUrl = swSettings.swsites.login + swSettings.components.HookWorkspace.resources.UserSubscriptionsUrl;

    return (
        <a
            data-automation={"manage-plan"}
            href={upgradeUrl}
            target={"_blank"}
            rel="noreferrer"
        >
            <Button type="flat">{translate("growth.homepage.nt.buttonText")}</Button>
        </a>
    );
};

const ProminentTarget = () => {
    const upgradeUrl = swSettings.swsites.login + swSettings.components.HookWorkspace.resources.UserSubscriptionsUrl;

    return (
        <PlainTooltip
            tooltipContent={translate("growth.homepage.promotionalBanner.target.tooltip")}
            placement="bottom"
            debounce={200}
        >
            <div style={{ width: "fit-content" }}>
                <a
                    href={upgradeUrl}
                    target={"_blank"}
                    rel="noreferrer"
                >
                    <StyledPromotionalIcon
                        iconName="open-new-window"
                        iconColor={colorsPalettes.blue[400]}
                    />
                </a>
            </div>
        </PlainTooltip>
    );
};

export const getPackageName = (): string => {
    const userPlan = swSettings.user.plan;
    return packagesDisplayName?.[userPlan] ?? userPlan;
};

interface IHomePageBannerProps {
    inviteUsersButton?: ReactElement;
    isProminent?: boolean;
}

export const HomePageBanner: React.FC<IHomePageBannerProps> = ({ inviteUsersButton, isProminent }) => {
    const daysLeft = getDaysLeft(swSettings);
    const trialEnded = daysLeft < 1;
    const packageName = getPackageName();
    const isRedirectToNtPackagesRequired = swSettings.components.HookWorkspace.resources.IsRedirectToNtPackagesRequired;

    if (isProminent) {
        return (
            <HomePageBannerWrapper>
                <StyledPromotionalPackageText>{packageName}</StyledPromotionalPackageText>
                <ProminentTarget />
                {inviteUsersButton}
            </HomePageBannerWrapper>
        );
    }

    if (!trialEnded) {
        return (
            <HomePageBannerWrapper data-automation={"homepage-banner"}>
                <BoldLeftDayText>
                    {translate(`growth.homepageTrial.layout.header.dayLeft.${daysLeft > 1 ? "plural" : "single"}`, {
                        number: daysLeft,
                    })}
                </BoldLeftDayText>
                <BannerTrialText>&nbsp;{translate("growth.homepageTrial.layout.header.dayLeft")}</BannerTrialText>
                {isRedirectToNtPackagesRequired ? <TrialBannerButton /> : null}
                {inviteUsersButton}
            </HomePageBannerWrapper>
        );
    }
    if (packageName) {
        return (
            <HomePageBannerWrapper data-automation={"homepage-banner"}>
                <BannerNoTouchText>
                    <span
                        dangerouslySetInnerHTML={{
                            __html: translate("growth.homepageTrial.layout.header.packageBanner", {
                                packageName,
                            }),
                        }}
                    />
                </BannerNoTouchText>
                <NoTouchBannerButton />
                {inviteUsersButton}
            </HomePageBannerWrapper>
        );
    }
    return null;
};
